<template>
  <LcModal
    is-visible
    :background-class="$style.prizeBannerModalBackground"
    :close-triggers="['click-outside', 'escape']"
    transition="slide-down-lg"
    :tracking="tracking"
    @close="$emit('close')"
  >
    <template #modal="{ setRef }">
      <LcCard
        :ref="setRef"
        body-class="wrap flex flex-col h-full p-none"
        class="prize-banner-modal lc-modal__card lc-modal__card--large bg-secondary-secondary-bold text-left"
        :class="{
          scrolled: isScrolled,
        }"
        radius="medium"
        role="dialog"
      >
        <header class="header">
          <PrizeBannerInner
            v-bind="{ drawLabel, headline, odds, visibleJackpot }"
            class="prize-banner-modal-banner"
            variant="modal"
          >
            <LcButton
              class="prize-banner-modal-banner-close-button"
              icon="close"
              no-padding
              variant="tertiary"
              @click="emit('close')"
            />
          </PrizeBannerInner>
        </header>

        <div
          ref="bodyRef"
          class="body px-200 py-200"
          :class="{ 'flex items-center': error }"
          @scroll="handleByScroll"
        >
          <slot v-if="!error" />
          <section v-else class="flex flex-col items-center">
            <h4 class="mb-200">{{ $t('info.expandable.error.headline') }}</h4>
            <i18n-t
              keypath="info.expandable.error.description"
              scope="global"
              tag="p"
              class="text-center prize-banner-modal-support"
            >
              <template #contactSupport>
                <a class="text-link" :href="$localePath('/service?chat=true')">
                  {{ $t('common.sentences.contactsupport') }}
                </a>
              </template>
            </i18n-t>
            <Illustration name="price-banner-error" class="mt-300" />
          </section>
        </div>
      </LcCard>
    </template>
  </LcModal>
</template>

<script lang="ts" setup>
import { TrackingImpressionProps } from '~~/layers/core/tracking/app/types/tracking'

type Emits = {
  (e: 'close'): void
}

type Props = {
  drawLabel?: string
  error?: boolean
  headline: string
  odds?: string
  visibleJackpot?: Money
}

defineProps<Props & TrackingImpressionProps>()

const emit = defineEmits<Emits>()

const bodyRef = ref<HTMLElement | null>(null)
const isScrolled = ref(false)

const handleByScroll = () => {
  if (!bodyRef.value) return
  isScrolled.value = bodyRef.value.scrollTop > 0
}
</script>

<style lang="scss" module>
.prize-banner-modal-background {
  overflow: hidden;
  padding: 4rem;

  @include media-tablet {
    padding: 0;
    background: none;
    backdrop-filter: none;
  }
}
</style>

<style lang="scss" scoped>
.prize-banner-modal {
  max-width: 44.875rem;

  &-banner {
    padding: token('size/space/gap/200');
    border-radius: token('size/radius/m') token('size/radius/m') 0 0;

    @include media-tablet {
      border-radius: 0;
    }

    &-close-button {
      position: absolute;
      top: 0;
      right: 0;
      z-index: 1;
      height: auto;
      padding: token('size/space/gap/100') !important;
      border: none;
    }
  }

  &-support {
    white-space: pre-line;
  }

  @include media-tablet {
    height: 100%;
    border-radius: 0;
  }

  :deep(.body) {
    flex: 1 1 auto;
    overflow-y: auto;
    background-color: token('color/neutral/white');
  }

  :deep(.header) {
    position: sticky;
    top: 0;
    box-shadow: token('shadow/shadow/card/large');
  }
}

.scrolled {
  :deep(.header) {
    z-index: 125;
    background-color: token('color/background/accent/win/win');
  }
}
</style>
